import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sitesAPI, activitiesAPI, homepageAPI, resolveImageUrl } from '@/lib/api';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Zap, Waves, Clock, MapPin, ChevronRight } from 'lucide-react';

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_multi-watt-hub/artifacts/mf05ovpb_Logo%20riding%20watt%202.png";
const DEFAULT_HERO = "https://images.unsplash.com/photo-1600970188293-313e1f61beb0?w=1600&q=80";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_ICONS = {
  efoil: Zap,
  esurf: Waves,
  paddle: Waves,
  wingfoil: Waves,
};

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sites, setSites] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [hp, setHp] = useState({});
  const isIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();

  useEffect(() => {
    sitesAPI.getAll().then(r => {
      setSites(r.data);
      if (r.data.length > 0) setSelectedSite(r.data[0].id);
    }).catch(() => {});
    homepageAPI.get().then(r => setHp(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedSite) {
      activitiesAPI.getAll(selectedSite).then(r => setActivities(r.data)).catch(() => {});
    }
  }, [selectedSite]);

  const selectedSiteData = sites.find(s => s.id === selectedSite);
  const resolveImg = (url, fallback) => {
    if (!url) return fallback;
    if (url.startsWith('/api/uploads')) return `${API_URL}${url}`;
    if (url.startsWith('/uploads')) return `${API_URL}/api${url}`;
    return url;
  };

  if (isIframe) {
    // Iframe mode: show only sites selection
    return (
      <div className="min-h-screen bg-[#0A0A0A]" data-testid="home-page-iframe">
        <section className="py-12 lg:py-16 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-10">
              <img src={DEFAULT_LOGO} alt="Riding Watt" className="h-12 mx-auto mb-4" />
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-white tracking-tight">
                Choisissez votre spot
              </h2>
              <p className="text-sm text-slate-500 mt-2">Sélectionnez un site pour voir les activités disponibles</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {sites.map((site) => (
                <div
                  key={site.id}
                  className="site-card h-[350px] group cursor-pointer"
                  onClick={() => navigate(`/activities/${site.id}`)}
                  data-testid={`site-card-${site.slug}`}
                >
                  <img
                    src={resolveImg(site.image_url)}
                    alt={site.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 z-10 flex flex-col justify-end p-8 md:p-10">
                    <div className="flex items-center gap-3 mb-3">
                      <MapPin className="w-8 h-8 text-[#00FF00] drop-shadow-[0_0_12px_rgba(0,255,0,0.6)]" />
                      <span className="text-2xl md:text-3xl uppercase tracking-wider text-[#00FF00] font-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">{site.city}</span>
                    </div>
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-white/80">{site.name}</h3>
                    <p className="text-sm text-white/50 mt-1.5 line-clamp-2">{site.description}</p>
                    <Button
                      className="mt-5 w-fit btn-pill bg-[#00FF00]/20 backdrop-blur-md text-[#00FF00] border border-[#00FF00]/30 hover:bg-[#00FF00] hover:text-black hover:scale-105 hover:shadow-[0_0_25px_rgba(0,255,0,0.4)] active:scale-95 transition-all duration-300 text-sm px-6 group/btn"
                      data-testid={`site-explore-${site.slug}`}
                    >
                      Voir les activités
                      <ChevronRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="home-page" className="bg-[#0A0A0A]">
      {/* Hero Section */}
      <section className="hero-section relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <img
          src={resolveImg(hp.hero_image, DEFAULT_HERO)}
          alt="Riding Watt"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 w-full">
          <div className="max-w-2xl">
            {hp.show_logo !== false && <img src={resolveImg(hp.logo_url, DEFAULT_LOGO)} alt="Riding Watt Logo" className="w-48 md:w-64 mb-8 animate-fade-in-up" data-testid="hero-logo" />}
            <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/30 mb-6 font-accent text-xs uppercase tracking-widest animate-fade-in-up">
              {hp.hero_badge || t('home.hero_badge')}
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up stagger-1">
              {hp.hero_title || t('home.hero_title')}<br />
              <span className="text-[#00FF00] text-glow">{hp.hero_title_accent || t('home.hero_title_accent')}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed mb-10 animate-fade-in-up stagger-2 max-w-lg">
              {hp.hero_description || t('home.hero_desc')}
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up stagger-3">
              <Button
                onClick={() => document.getElementById('sites-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_30px_rgba(0,255,0,0.4)] text-base px-8 py-6 font-bold"
                data-testid="hero-book-now-btn"
              >
                {hp.hero_cta || t('home.hero_cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => navigate('/gift-cards')}
                variant="outline"
                className="btn-pill border-2 border-[#00FF00]/40 text-[#00FF00] hover:bg-[#00FF00]/10 backdrop-blur-sm text-base px-8 py-6"
                data-testid="hero-gift-card-btn"
              >
                {hp.hero_gift_cta || t('home.hero_gift_cta')}
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative wave */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 40C360 80 720 0 1080 40C1260 60 1380 80 1440 80V100H0V40Z" fill="#0A0A0A" />
          </svg>
        </div>
      </section>

      {/* Sites Section */}
      <section id="sites-section" className="py-20 lg:py-32 relative noise-overlay bg-[#0A0A0A]" data-testid="sites-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="mb-16">
            <p className="font-accent text-sm uppercase tracking-widest text-[#00FF00] font-bold mb-3">{hp.sites_subtitle || t('home.sites_badge')}</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-tight">
              {hp.sites_title || t('home.sites_title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {sites.map((site) => (
              <div
                key={site.id}
                className={`site-card h-[400px] group ${selectedSite === site.id ? 'ring-4 ring-[#00FF00] ring-offset-4 ring-offset-[#0A0A0A]' : ''}`}
                onClick={() => setSelectedSite(site.id)}
                data-testid={`site-card-${site.slug}`}
              >
                <img
                  src={resolveImg(site.image_url)}
                  alt={site.name}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 z-10 flex flex-col justify-end p-8 md:p-10">
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin className="w-8 h-8 text-[#00FF00] drop-shadow-[0_0_12px_rgba(0,255,0,0.6)]" />
                    <span className="text-2xl md:text-3xl uppercase tracking-wider text-[#00FF00] font-bold drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]" data-testid={`site-city-${site.slug}`}>{site.city}</span>
                  </div>
                  <h3 className="font-heading text-lg md:text-xl font-semibold text-white/80">{site.name}</h3>
                  <p className="text-sm text-white/50 mt-1.5 line-clamp-2">{site.description}</p>
                  <Button
                    onClick={(e) => { e.stopPropagation(); navigate(`/activities/${site.id}`); }}
                    className="mt-5 w-fit btn-pill bg-[#00FF00]/20 backdrop-blur-md text-[#00FF00] border border-[#00FF00]/30 hover:bg-[#00FF00] hover:text-black hover:scale-105 hover:shadow-[0_0_25px_rgba(0,255,0,0.4)] active:scale-95 transition-all duration-300 text-sm px-6 group/btn"
                    data-testid={`site-explore-${site.slug}`}
                  >
                    {t('home.explore_activities')}
                    <ChevronRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Activities Preview - REMOVED */}

      {/* Gift Card CTA */}
      <section className="py-20 lg:py-28 bg-[#0A0A0A]" data-testid="gift-card-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl bg-[#111111] border border-[#1A1A1A] p-10 md:p-16">
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#00FF00]/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/30 mb-6 font-accent text-xs uppercase tracking-widest">
                  {hp.gift_badge || t('home.gift_badge')}
                </Badge>
                <h2 className="font-heading text-3xl md:text-5xl font-bold text-white tracking-tight mb-4">
                  {hp.gift_title || t('home.gift_title')}
                </h2>
                <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-8">
                  {hp.gift_description || t('home.gift_desc')}
                </p>
                <Button
                  onClick={() => navigate('/gift-cards')}
                  className="btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_30px_rgba(0,255,0,0.3)] font-bold text-base px-8 py-6"
                  data-testid="cta-gift-card-btn"
                >
                  {hp.gift_cta || t('home.gift_cta')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
              <div className="hidden md:flex justify-center">
                <div className="w-72 h-44 rounded-2xl bg-[#00FF00] shadow-[0_0_40px_rgba(0,255,0,0.2)] flex flex-col justify-between p-6 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="flex justify-between items-start">
                    <span className="font-heading text-sm font-bold text-black">RIDINGWATT</span>
                    <span className="font-heading text-xs font-bold text-black/50">CARTE CADEAU</span>
                  </div>
                  <div>
                    <p className="text-xs text-black/50 mb-1">Valeur</p>
                    <p className="font-heading text-3xl font-bold text-black">50€</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
